import classNames from 'classnames';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Back from '@/assets/icons/back.svg?react';
import Gear from '@/assets/icons/gear.svg?react';
import Profile from '@/assets/icons/profile.svg?react';
import { adminTenantEndpoint } from '@/consts';
import { isCloud } from '@/consts/env';
import { AppThemeContext } from '@/contexts/AppThemeProvider';
import Spacer from '@/ds-components/Spacer';
import useTenantPathname from '@/hooks/use-tenant-pathname';

import MellowMark from '../../../../toolkit/core-kit/assets/mellow-mark.svg?react';

import EnterpriseSubscriptions from './EnterpriseSubscriptions';
import InkeepAskAi from './InkeepAskAi';
import TenantSelector from './TenantSelector';
import UserInfo from './UserInfo';
import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  /* eslint-disable react/boolean-prop-naming */
  readonly hideTenantSelector?: boolean;
  /* eslint-enable react/boolean-prop-naming */
};

function Topbar({ className, hideTenantSelector }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'admin_console' });
  const { navigate, getTo, match } = useTenantPathname();
  const { embeddedInPortal } = useContext(AppThemeContext);

  if (embeddedInPortal || !isCloud) {
    const canGoBack = window.history.state?.idx > 0;
    return (
      <nav aria-label="Identity workbench" className={classNames(styles.workbench, className)}>
        <span className={styles.wordmark}>
          <MellowMark aria-hidden />
          Mellow
        </span>
        <button
          type="button"
          title={t('general.back')}
          aria-label={t('general.back')}
          disabled={!canGoBack && match('/dashboard', true)}
          onClick={() => {
            if (canGoBack) {
              void navigate(-1);
            } else {
              void navigate('/dashboard', { replace: true });
            }
          }}
        >
          <Back aria-hidden />
        </button>
        <Link to={getTo('/dashboard')} title="Identity dashboard" aria-label="Identity dashboard">
          <svg aria-hidden viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="m12 2 10 8.6-1.4 1.6L20 11.7V21h-6v-6h-4v6H4v-9.3l-.6.5L2 10.6z" />
          </svg>
        </Link>
        <span className={styles.sectionName}>Identity Management</span>
        <Spacer />
        <a
          href="https://developer.visca.ai/support"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Developer portal help"
          title="Developer portal help"
        >
          <span className={styles.help}>?</span>
        </a>
        <a
          className={styles.profile}
          href={new URL('/account/profile', adminTenantEndpoint).href}
          target="_blank"
          rel="noopener noreferrer"
          title={t('menu.profile')}
        >
          <Profile aria-hidden />
          {t('menu.profile')}
        </a>
        <Link
          to={getTo('/tenant-settings')}
          aria-label="Identity settings"
          title="Identity settings"
        >
          <Gear aria-hidden />
        </Link>
      </nav>
    );
  }

  return (
    <div className={classNames(styles.topbar, className)}>
      <button
        type="button"
        className={styles.brandButton}
        aria-label="Go to dashboard"
        onClick={() => {
          void navigate('/');
        }}
      >
        <MellowMark aria-hidden className={styles.logo} />
        <span>Identity Management</span>
      </button>
      {!hideTenantSelector && <TenantSelector />}
      <Spacer />
      <div className={styles.rightRail}>
        <InkeepAskAi className={styles.button} />
        <EnterpriseSubscriptions className={styles.button} />
        <UserInfo />
      </div>
    </div>
  );
}

export default Topbar;
